import React, { useState } from 'react';
import {
  Popover,
  Button,
  ActionIcon,
  Indicator,
  Text,
  Group,
  Stack,
  ScrollArea,
  Tabs,
  Badge,
  Divider,
  Box,
  ThemeIcon,
  UnstyledButton,
  Menu,
  rem,
} from '@mantine/core';
import {
  IconBell,
  IconCheck,
  IconTrash,
  IconDots,
  IconAlertCircle,
  IconInfoCircle,
  IconUrgent,
  IconClock,
  IconSettings,
  IconBellOff,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
  useClearAllNotifications,
} from '../../hooks/useNotifications';
import { SmartLoader } from './LoadingStates';
import type { Notification } from '../../types/api';

interface NotificationCenterProps {
  buttonProps?: {
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    variant?: string;
  };
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ buttonProps }) => {
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('all');

  const { data, isLoading, error } = useNotifications();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();
  const deleteNotification = useDeleteNotification();
  const clearAll = useClearAllNotifications();

  const notifications = (data?.data || []).filter(Boolean);
  const unreadCount = data?.unreadCount || 0;

  const filteredNotifications = notifications.filter((notification) => {
    if (!notification) return false;
    if (activeTab === 'all') return true;
    if (activeTab === 'unread') return !notification.read_at;
    return notification.priority === activeTab;
  });

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read_at) {
      await markAsRead.mutateAsync(notification.id);
    }
    
    // Navigate based on notification type
    const metadata = notification.metadata as { link?: string } | undefined;
    if (metadata?.link) {
      navigate(metadata.link);
      setOpened(false);
    }
  };

  const getNotificationIcon = (notification: Notification) => {
    switch (notification.priority) {
      case 'critical':
        return <IconUrgent size={20} />;
      case 'high':
        return <IconAlertCircle size={20} />;
      default:
        return <IconInfoCircle size={20} />;
    }
  };

  const getNotificationColor = (notification: Notification) => {
    switch (notification.priority) {
      case 'critical':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'yellow';
      default:
        return 'blue';
    }
  };

  const NotificationItem: React.FC<{ notification: Notification }> = ({ notification }) => {
    const [showMenu, setShowMenu] = useState(false);

    return (
      <UnstyledButton
        onClick={() => handleNotificationClick(notification)}
        onMouseEnter={() => setShowMenu(true)}
        onMouseLeave={() => setShowMenu(false)}
        style={{
          width: '100%',
          padding: '12px',
          borderRadius: '8px',
          backgroundColor: notification.read_at ? 'transparent' : 'var(--mantine-color-blue-0)',
          transition: 'all 200ms ease',
          ':hover': {
            backgroundColor: 'var(--mantine-color-gray-0)',
          },
        }}
      >
        <Group gap="sm" align="flex-start" wrap="nowrap">
          <ThemeIcon
            color={getNotificationColor(notification)}
            variant={notification.read_at ? 'light' : 'filled'}
            size="md"
          >
            {getNotificationIcon(notification)}
          </ThemeIcon>
          
          <Box style={{ flex: 1 }}>
            <Group justify="space-between" gap="xs" mb={4}>
              <Text size="sm" fw={notification.read_at ? 400 : 600} lineClamp={1}>
                {notification.title || 'Notification'}
              </Text>
              {showMenu && (
                <Menu shadow="md" width={150}>
                  <Menu.Target>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <IconDots size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {!notification.read_at && (
                      <Menu.Item
                        leftSection={<IconCheck style={{ width: rem(14), height: rem(14) }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead.mutate(notification.id);
                        }}
                      >
                        Mark as read
                      </Menu.Item>
                    )}
                    <Menu.Item
                      leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                      color="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification.mutate(notification.id);
                      }}
                    >
                      Delete
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
            
            <Text size="xs" c="dimmed" lineClamp={2}>
              {notification.body || notification.message}
            </Text>
            
            <Group gap="xs" mt={4}>
              {notification.notification_type && (
                <Badge size="xs" variant="dot" color={getNotificationColor(notification)}>
                  {notification.notification_type}
                </Badge>
              )}
              <Text size="xs" c="dimmed">
                <IconClock size={12} style={{ verticalAlign: 'middle' }} />{' '}
                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
              </Text>
            </Group>
          </Box>
        </Group>
      </UnstyledButton>
    );
  };

  return (
    <Popover
      width={400}
      position="bottom-end"
      shadow="md"
      opened={opened}
      onChange={setOpened}
    >
      <Popover.Target>
        <Indicator
          inline
          label={unreadCount}
          size={16}
          disabled={unreadCount === 0}
          color="red"
          withBorder
        >
          <ActionIcon
            size={buttonProps?.size || 'md'}
            variant={buttonProps?.variant || 'subtle'}
            onClick={() => setOpened((o) => !o)}
          >
            <IconBell size={20} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack gap={0}>
          <Group justify="space-between" px="md" py="sm">
            <Text fw={600} size="lg">Notifications</Text>
            <Group gap="xs">
              <ActionIcon
                size="sm"
                variant="subtle"
                onClick={() => navigate('/settings/notifications')}
              >
                <IconSettings size={16} />
              </ActionIcon>
              {unreadCount > 0 && (
                <Button
                  size="xs"
                  variant="subtle"
                  leftSection={<IconCheck size={14} />}
                  onClick={() => markAllAsRead.mutate()}
                  loading={markAllAsRead.isPending}
                >
                  Mark all read
                </Button>
              )}
            </Group>
          </Group>

          <Divider />

          <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'all')}>
            <Tabs.List px="md">
              <Tabs.Tab value="all">
                All
                {notifications.length > 0 && (
                  <Badge size="xs" variant="filled" ml={4}>
                    {notifications.length}
                  </Badge>
                )}
              </Tabs.Tab>
              <Tabs.Tab value="unread">
                Unread
                {unreadCount > 0 && (
                  <Badge size="xs" variant="filled" color="red" ml={4}>
                    {unreadCount}
                  </Badge>
                )}
              </Tabs.Tab>
              <Tabs.Tab value="high">High Priority</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="all">
              <ScrollArea h={400} px="md" py="sm">
                <SmartLoader
                  isLoading={isLoading}
                  error={error as Error | null}
                  isEmpty={filteredNotifications.length === 0}
                  emptyState={
                    <Stack align="center" gap="md" py="xl">
                      <ThemeIcon size="xl" variant="light" color="gray">
                        <IconBellOff size={24} />
                      </ThemeIcon>
                      <Text size="sm" c="dimmed">
                        {activeTab === 'unread'
                          ? 'No unread notifications'
                          : 'No notifications'}
                      </Text>
                    </Stack>
                  }
                >
                  <Stack gap="xs">
                    {filteredNotifications.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                      />
                    ))}
                  </Stack>
                </SmartLoader>
              </ScrollArea>
            </Tabs.Panel>
          </Tabs>

          {notifications.length > 0 && (
            <>
              <Divider />
              <Group justify="center" p="sm">
                <Button
                  variant="subtle"
                  size="sm"
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  onClick={() => clearAll.mutate()}
                  loading={clearAll.isPending}
                >
                  Clear all notifications
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export default NotificationCenter;